import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Backend intentionally returns 200 whether or not the email exists
      // (no user enumeration). Only true infrastructure failures land here.
      toast.error(formatError(err.response?.data?.detail) || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center" data-testid="forgot-password-sent">
        <CheckCircle2 className="w-14 h-14 mx-auto text-accent mb-6" strokeWidth={1.5} />
        <div className="label-caps text-primary/70 mb-3">Check your inbox</div>
        <h1 className="font-display text-3xl">If an account exists for that email, we sent you a reset link.</h1>
        <p className="mt-4 text-primary/70">The link expires in one hour. Look in your spam folder if you don't see it in a minute.</p>
        <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-primary hover:underline underline-offset-4" data-testid="forgot-back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-24">
      <div className="label-caps text-accent mb-3">Password reset</div>
      <h1 className="font-display text-4xl text-primary">Forgot your password?</h1>
      <p className="text-primary/70 mt-3 text-sm">
        Type in the email you used to create your VDE portal account. We'll email you a one-time link to set a new password.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-5" data-testid="forgot-password-form">
        <div>
          <Label>Email</Label>
          <div className="mt-2 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/40" />
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none pl-10"
              placeholder="you@example.com"
              data-testid="forgot-password-email"
            />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="forgot-password-submit">
          {loading ? "Sending…" : "Send reset link"}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground mt-8 text-center">
        Remembered it? <Link to="/login" className="text-primary link-underline" data-testid="forgot-signin-link">Sign in</Link>
      </p>
    </div>
  );
}
